/**
 * ChatWindow — ventana de conversación principal.
 *
 * R-PT-06: lista y reapertura de chats; muestra "de qué va" al abrir.
 * R-PT-07: streaming async (progress → answer/panel/clarify/escalated → done).
 * R-PT-08: panel multi-agente con voces separadas y atribuidas.
 * R-PT-09: sin estado autoritativo en cliente; rehidrata desde API.
 * R-PT-11: ActionPreview con aprobación explícita.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Loader, ArrowUp } from 'lucide-react'
import { getChatHistory, sendMessage } from '../api/client.js'
import { useSession } from '../context/SessionContext.jsx'
import { AgentAvatar } from './AgentAvatar.jsx'
import { agentColorOf } from '../lib/agent-colors.js'
import { avatarUrlOf } from '../lib/agent-avatars.js'
import AutoGrowTextarea from './AutoGrowTextarea.jsx'
import Home from './Home.jsx'
import AnswerEvent       from './events/AnswerEvent.jsx'
import ClarifyEvent      from './events/ClarifyEvent.jsx'
import ProgressEvent     from './events/ProgressEvent.jsx'
import PanelEvent        from './events/PanelEvent.jsx'
import ActionPreviewEvent from './events/ActionPreviewEvent.jsx'
import EscalatedEvent    from './events/EscalatedEvent.jsx'

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

function formatTime(isoString) {
  const d = new Date(isoString)
  // Turnos antiguos del kernel pueden venir sin created_at: sin hora.
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('es-ES', {
    hour: '2-digit', minute: '2-digit',
  })
}

// ---------------------------------------------------------------------------
// Burbuja de mensaje del usuario
// ---------------------------------------------------------------------------
function UserBubble({ message }) {
  return (
    <div className="msg msg--user">
      <div className="msg__bubble msg__bubble--user">
        {message.content}
      </div>
      <span className="msg__time">{formatTime(message.timestamp)}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Burbuja de mensaje del agente
// ---------------------------------------------------------------------------
// Píldora "derivado a" (#280 SPEC §C.2): solo se pinta si el turno TRAE el
// dato de derivación. Todavía no viaja por SSE — este campo es tolerante y
// no se inventa; hasta que #281 lo defina, esto queda inerte en producción.
function DerivationPill({ derivation, agentColorFor }) {
  if (!derivation?.nombre) return null
  const color = agentColorFor(derivation.id ?? derivation.nombre)
  return (
    <div className="derivation-pill">
      <span className="derivation-pill__arrow">→ Derivado a</span>
      <AgentAvatar name={derivation.nombre} avatarUrl={avatarUrlOf(derivation)} size={18} ringColor={color} />
      <strong>{derivation.nombre}</strong>
      {derivation.proposito && (
        <span className="derivation-pill__proposito">· {derivation.proposito}</span>
      )}
    </div>
  )
}

function AgentBubble({ message, onReply, resolveAgentName, resolveAgentRole, resolveAgentAvatar, agentColorFor }) {
  const agentName = resolveAgentName(message.agent)
  const agentRole = resolveAgentRole(message.agent)
  const agentPhoto = resolveAgentAvatar(message.agent)
  const voiceColor = agentColorFor(message.agent?.id ?? message.agent?.nombre ?? agentName)

  function renderEvent() {
    switch (message.type) {
      case 'answer':
        return <AnswerEvent payload={message.payload} />
      case 'clarify':
        return <ClarifyEvent payload={message.payload} onReply={onReply} />
      case 'progress':
        return <ProgressEvent payload={message.payload} />
      case 'panel':
        return <PanelEvent payload={message.payload} />
      case 'action_preview':
        return <ActionPreviewEvent payload={message.payload} />
      case 'escalated':
        return <EscalatedEvent payload={message.payload} />
      default:
        return <p style={{ color: 'var(--color-text-muted)' }}>[evento desconocido: {message.type}]</p>
    }
  }

  // Eventos de progreso: compactos, sin avatar
  if (message.type === 'progress') {
    return (
      <div className="msg msg--agent msg--progress">
        <ProgressEvent payload={message.payload} />
      </div>
    )
  }

  return (
    <div className="msg msg--agent">
      <div className="msg__avatar">
        <AgentAvatar name={agentName} avatarUrl={agentPhoto} size={28} ringColor={voiceColor} />
      </div>
      <div className="msg__body" style={voiceColor ? { '--voice-color': voiceColor } : undefined}>
        <span className="msg__agent-name-row">
          <span className="msg__agent-name">{agentName}</span>
          {agentRole && (
            <span
              className="agent-role-badge"
              style={voiceColor ? { '--badge-color': voiceColor } : undefined}
            >
              {agentRole}
            </span>
          )}
        </span>
        <div className={'msg__bubble msg__bubble--agent' + (voiceColor ? ' msg__bubble--voz' : '')}>
          {renderEvent()}
        </div>
        <span className="msg__time">{formatTime(message.timestamp)}</span>
        <DerivationPill derivation={message.payload?.derivado_a} agentColorFor={agentColorFor} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Estado de chat bloqueado por escalado
// ---------------------------------------------------------------------------
function BlockedBanner() {
  return (
    <div className="chat-blocked-banner">
      <span className="chat-blocked-banner__inner">
        <Loader className="icon-spin" size={15} strokeWidth={1.8} aria-hidden="true" /> Esperando respuesta del experto
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ChatWindow
// ---------------------------------------------------------------------------
// Nombre "amable" a partir de un id de agente (fallback si no está en la lista).
function titleCase(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

export default function ChatWindow({ chatId, chatMeta, isDraft, onFirstSend, onSourcesChange, onSelectChat, onSelectAgent }) {
  const { agents, agentPalette, resolvedTheme, user, chats } = useSession()
  const [messages, setMessages]   = useState([])
  const [input, setInput]         = useState('')
  const [streaming, setStreaming] = useState(false)
  const [blocked, setBlocked]     = useState(false)
  const [loading, setLoading]     = useState(false)

  // US-02: agente destino del hilo. Un borrador dirigido (clic en el rail) nace
  // con el destino en su metadata; al reabrir un chat, el historial lo aporta.
  // null = sin dirigir -> Anne-en-cliente enruta (US-03).
  const [threadAgentId, setThreadAgentId] = useState(chatMeta?.agente_destino ?? null)

  // id -> nombre desde la lista de agentes del descriptor (US-01). Resuelve la
  // atribución de las burbujas del historial (que llegan con id) y la cabecera.
  const resolveAgentName = useCallback((agent) => {
    if (agent?.nombre) return agent.nombre
    const id = agent?.id ?? agent
    if (!id) return 'Anne'
    const found = agents.find(a => a.id === id)
    return found?.nombre ?? titleCase(id)
  }, [agents])

  // Rol del agente (para el badge de voz en el hilo). Se resuelve del
  // descriptor por id/nombre; sin match, sin badge (no se inventa un rol).
  const resolveAgentRole = useCallback((agent) => {
    const id = agent?.id ?? agent
    const found = agents.find(a => a.id === id || a.nombre === (agent?.nombre ?? id))
    return found?.rol ?? null
  }, [agents])

  // Foto del agente para el hilo: el avatar_url del descriptor si el kernel lo
  // emite, si no el mapa del front por id (avatarUrlOf). Cae a iniciales.
  const resolveAgentAvatar = useCallback((agent) => {
    const id = agent?.id ?? agent
    const found = agents.find(a => a.id === id || a.nombre === (agent?.nombre ?? id))
    return avatarUrlOf(found ?? { id, avatar_url: agent?.avatar_url })
  }, [agents])

  const threadAgentName = threadAgentId
    ? resolveAgentName({ id: threadAgentId })
    : (chatMeta?.agente_nombre ?? 'Anne')

  // US-03: distinguir "hablando con Anne (orquesta y dirige)" de "hablando con
  // un agente de línea (directo)". Data-driven por el rol del descriptor, no
  // por el nombre: sin destino explícito el interlocutor es la orquestadora;
  // con destino, se mira su rol. Así, cuando se conecte otro orquestador, el
  // indicador se comporta igual sin tocar código.
  const threadAgent = threadAgentId ? agents.find(a => a.id === threadAgentId) : null
  const esOrquestadora = !threadAgentId || /orquest/i.test(threadAgent?.rol ?? '')

  // Color de identidad por agente (#280): id o nombre -> color de
  // resolveAgentPalette (memoizada en SessionContext). R-PT-08: cada voz
  // siempre con su color; sin entrada en la paleta, sin anillo (fallback hash).
  const agentColorFor = useCallback((idOrName) => {
    if (!idOrName) return null
    const id = agents.find(a => a.id === idOrName || a.nombre === idOrName)?.id ?? idOrName
    return agentColorOf(agentPalette ?? {}, id, resolvedTheme)
  }, [agents, agentPalette, resolvedTheme])

  // Fuentes citadas del hilo (#280 RightRail): se derivan de los `sources`
  // que traen los `answer` ya recibidos. Solo lo que ha llegado; no se
  // inventa nada si un turno no cita fuentes.
  const sources = useMemo(() => {
    const seen = new Map()
    for (const m of messages) {
      for (const s of m.payload?.sources ?? []) {
        const key = s.ref ?? s.label
        if (key && !seen.has(key)) seen.set(key, s)
      }
    }
    return [...seen.values()]
  }, [messages])

  useEffect(() => { onSourcesChange?.(sources) }, [sources, onSourcesChange])

  const bottomRef   = useRef(null)
  const cancelRef   = useRef(null)
  const inputRef    = useRef(null)
  // Capturados al montar: la ventana se remonta por key al cambiar de chat,
  // así que no cambian durante la vida del componente (aunque isDraft mute
  // en el padre al registrarse el chat tras el primer envío).
  const isDraftRef   = useRef(isDraft)
  const firstSendRef = useRef(isDraft)

  // Cargar historial al abrir un chat existente (R-PT-06).
  // Conversación nueva (borrador): empieza vacía, sin llamada a la API.
  useEffect(() => {
    setMessages([])
    setBlocked(false)
    if (isDraftRef.current) return
    setLoading(true)
    getChatHistory(chatId)
      .then(data => {
        setMessages(data.messages)
        // US-02: el historial dice con qué agente es el hilo -> cabecera.
        if (data.agente_destino) setThreadAgentId(data.agente_destino)
      })
      .catch(err => console.error('getChatHistory:', err))
      .finally(() => setLoading(false))
    return () => cancelRef.current?.cancel()
  }, [chatId])

  // Auto-scroll al último mensaje
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSend = useCallback(async (text) => {
    const content = (text ?? input).trim()
    if (!content || streaming || blocked) return

    // 1. Añadir el mensaje del usuario al hilo
    const userMsg = {
      id:        `user-${Date.now()}`,
      role:      'user',
      content,
      timestamp: new Date().toISOString(),
    }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    setStreaming(true)

    // Primer mensaje de un borrador: refrescar SOLO el título en memoria del
    // padre (estado efímero de UI). US-65/R-PT-09: no se persiste nada — la
    // conversación entra en la lista cuando el kernel la incluya en
    // `descriptor.chats` del siguiente bootstrap.
    if (firstSendRef.current) {
      firstSendRef.current = false
      onFirstSend?.(content)
    }

    // ID del progress placeholder activo en el hilo.
    // Se muta de forma síncrona (fuera de setState) para evitar
    // condiciones de carrera con las actualizaciones asíncronas de React.
    let currentProgressId = null

    function onEvent(event) {
      if (event.type === 'done') return

      const msgBase = {
        id:        event.message_id ?? `evt-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        role:      'agent',
        // US-02: atribuir al agente del hilo (id -> nombre vía resolveAgentName).
        // Sin destino explícito, el interlocutor es Anne-en-cliente (US-03).
        agent:     { id: threadAgentId ?? 'anne' },
        type:      event.type,
        payload:   event.payload,
        timestamp: new Date().toISOString(),
      }

      if (event.type === 'progress') {
        if (!currentProgressId) {
          // Primer progress del turno: añadir al hilo
          currentProgressId = msgBase.id
          setMessages(prev => [...prev, msgBase])
        } else {
          // Progress sucesivo: reemplazar el anterior en sitio
          const pid = currentProgressId
          setMessages(prev => prev.map(m => m.id === pid ? { ...msgBase, id: pid } : m))
        }
      } else if (event.type === 'answer') {
        // Los `answer` del kernel llegan por TROZOS (streaming §5, B2a):
        // mismo message_id -> concatenar sobre el bloque en curso;
        // message_id nuevo -> bloque nuevo.
        const pid = currentProgressId
        currentProgressId = null
        setMessages(prev => {
          const base = pid ? prev.filter(m => m.id !== pid) : prev
          const last = base[base.length - 1]
          if (last?.type === 'answer' && last.id === msgBase.id) {
            return [
              ...base.slice(0, -1),
              {
                ...last,
                payload: {
                  ...last.payload,
                  markdown: (last.payload?.markdown ?? '') + (event.payload?.markdown ?? ''),
                },
              },
            ]
          }
          return [...base, msgBase]
        })
      } else {
        // Evento final: quitar el progress y añadir el evento real
        const pid = currentProgressId
        currentProgressId = null
        setMessages(prev => {
          const sin_progress = pid ? prev.filter(m => m.id !== pid) : prev
          return [...sin_progress, msgBase]
        })
        if (event.type === 'escalated') setBlocked(true)
      }
    }

    function onDone() {
      setStreaming(false)
    }

    function onError(err) {
      console.error('SSE error:', err)
      setStreaming(false)
    }

    const handle = sendMessage(chatId, content, {
      onEvent, onDone, onError,
      agenteDestino: threadAgentId ?? undefined,  // US-02: dirige el turno
    })
    cancelRef.current = handle
  }, [chatId, input, streaming, blocked, onFirstSend, threadAgentId])

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Chat vacío (nuevo)
  const isEmpty = !loading && messages.length === 0
  // «Home» (equipo + compositor): SOLO en un borrador SIN dirigir
  // (threadAgentId == null), es decir, el chat que enruta Anne. Un borrador
  // dirigido a un agente CONCRETO —incluida Anne al pulsar su tarjeta— abre su
  // hilo dirigido, no la Home. Ojo: gatear por `esOrquestadora` reabriría la
  // Home en bucle al elegir a la orquestadora (el destino sería Anne y su rol
  // encaja con /orquest/); por eso se gatea por «sin destino».
  const showHome = isEmpty && isDraftRef.current && threadAgentId == null

  if (showHome) {
    return (
      <div className="chat-window">
        <Home
          user={user}
          agents={agents}
          chats={chats}
          palette={agentPalette}
          theme={resolvedTheme}
          onSend={text => handleSend(text)}
          onSelectAgent={onSelectAgent ?? (() => {})}
          onSelectChat={onSelectChat ?? (() => {})}
        />
      </div>
    )
  }

  return (
    <div className="chat-window">
      {/* Header */}
      <div className="chat-header">
        <div className="chat-header__meta">
          <span className="chat-header__title">
            {chatMeta ? chatMeta.titulo : 'Nueva conversación'}
          </span>
          {chatMeta?.de_que_va && (
            <span className="chat-header__desc">{chatMeta.de_que_va}</span>
          )}
        </div>
        {/* US-02/03: interlocutor visible del hilo (Anne por defecto). El
            sufijo distingue a la orquestadora (que enruta) del agente directo. */}
        <div
          className="chat-header__interlocutor"
          title={esOrquestadora
            ? `Hablando con ${threadAgentName}, que orquesta y dirige al agente adecuado`
            : `Hablando directamente con ${threadAgentName}`}
        >
          <AgentAvatar name={threadAgentName} avatarUrl={resolveAgentAvatar({ id: threadAgentId, nombre: threadAgentName })} size={22} />
          <span className="chat-header__interlocutor-label">
            Hablando con <strong>{threadAgentName}</strong>
            <span className="chat-header__interlocutor-role">
              {esOrquestadora ? ' · orquesta y dirige' : ' · directo'}
            </span>
          </span>
        </div>
      </div>

      {/* Hilo */}
      <div className="chat-thread" role="log" aria-live="polite" aria-atomic="false">
        {loading && (
          <p className="chat-loading">Cargando conversación…</p>
        )}

        {isEmpty && !loading && (
          <div className="chat-empty">
            <p className="chat-empty__text">
              {esOrquestadora
                ? `Escribe un mensaje para empezar.`
                : `Escribe un mensaje para ${threadAgentName}.`}
            </p>
            <p className="chat-empty__hint">
              {esOrquestadora
                ? `Estás hablando con ${threadAgentName}: si no eliges un agente, te dirigirá al más adecuado.`
                : `Estás hablando directamente con ${threadAgentName}.`}
            </p>
          </div>
        )}

        {messages.map(msg =>
          msg.role === 'user'
            ? <UserBubble key={msg.id} message={msg} />
            : <AgentBubble
                key={msg.id}
                message={msg}
                onReply={text => handleSend(text)}
                resolveAgentName={resolveAgentName}
                resolveAgentRole={resolveAgentRole}
                resolveAgentAvatar={resolveAgentAvatar}
                agentColorFor={agentColorFor}
              />
        )}

        <div ref={bottomRef} />
      </div>

      {/* Banner de bloqueo por escalado */}
      {blocked && <BlockedBanner />}

      {/* Campo de entrada */}
      <div className="chat-input-bar">
        <AutoGrowTextarea
          ref={inputRef}
          className="chat-input"
          placeholder={blocked ? 'Esperando al experto…' : 'Escribe un mensaje… (Enter para enviar)'}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={streaming || blocked}
          aria-label="Mensaje"
        />
        <button
          className="chat-send-btn"
          onClick={() => handleSend()}
          disabled={streaming || blocked || !input.trim()}
          aria-label="Enviar"
        >
          {streaming ? (
            <span className="chat-send-btn__spinner"><Loader className="icon-spin" size={18} strokeWidth={1.9} aria-hidden="true" /></span>
          ) : (
            <ArrowUp size={18} strokeWidth={1.9} aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  )
}
